import { Vector3 } from 'three';
import { Centerline } from './centerline';
import { RollKey, rollAt, shortestAngleDelta } from './roll';

/** Riders sit ~1.1 m above the rails; the authored spline is the heartline. */
export const HEARTLINE_OFFSET = 1.1;

export interface Frame {
  s: number;
  pos: Vector3; // heartline position
  tan: Vector3; // unit tangent
  up: Vector3; // after roll
  right: Vector3; // after roll; right = tan × up
  rollDeg: number;
  /** rail-centerline position = heartline − up · HEARTLINE_OFFSET */
  railPos: Vector3;
}

/** Parallel-transport an up vector along samples (double-reflection RMF). */
function transportRmf(pos: Vector3[], tan: Vector3[]): Vector3[] {
  const n = pos.length;
  const up: Vector3[] = new Array(n);
  const first = new Vector3(0, 1, 0);
  if (Math.abs(tan[0].y) > 0.99) first.set(1, 0, 0); // vertical start tangent
  first.addScaledVector(tan[0], -first.dot(tan[0])).normalize();
  up[0] = first;

  const v1 = new Vector3();
  const v2 = new Vector3();
  const riL = new Vector3();
  const tiL = new Vector3();
  for (let i = 0; i < n - 1; i++) {
    v1.subVectors(pos[i + 1], pos[i]);
    const c1 = v1.lengthSq();
    if (c1 < 1e-14) {
      up[i + 1] = up[i].clone();
      continue;
    }
    riL.copy(up[i]).addScaledVector(v1, (-2 / c1) * v1.dot(up[i]));
    tiL.copy(tan[i]).addScaledVector(v1, (-2 / c1) * v1.dot(tan[i]));
    v2.subVectors(tan[i + 1], tiL);
    const c2 = v2.lengthSq();
    const u = riL.clone();
    if (c2 > 1e-14) u.addScaledVector(v2, (-2 / c2) * v2.dot(u));
    // keep exactly perpendicular to the tangent (fights numerical drift)
    u.addScaledVector(tan[i + 1], -u.dot(tan[i + 1])).normalize();
    up[i + 1] = u;
  }
  return up;
}

/**
 * Rotation-minimising frames plus authored roll about the tangent.
 * Chart 0° / no override follows the track's own RMF (not gravity). Gravity
 * bank pins are converted into this relative roll when keys are built.
 *
 * On closed tracks RMF holonomy is cancelled so the seam meets, and consecutive
 * roll samples are unwrapped so a ±360n branch cut does not spin the rails.
 */
export function buildFrames(
  centerline: Centerline,
  rollKeys: RollKey[],
  sValues: number[],
): Frame[] {
  const n = sValues.length;
  if (n === 0) return [];
  const closed = centerline.track.closed;
  const L = centerline.totalLength;

  const pos: Vector3[] = new Array(n);
  const tan: Vector3[] = new Array(n);
  for (let i = 0; i < n; i++) {
    pos[i] = centerline.posAt(sValues[i]);
    tan[i] = centerline.tanAt(sValues[i]);
  }

  const rmfUp = transportRmf(pos, tan);

  let holonomy = 0;
  if (closed && n > 2) {
    const endUp = rmfUp[n - 1];
    const refUp = rmfUp[0];
    const refRight = new Vector3().crossVectors(tan[n - 1], refUp).normalize();
    holonomy = Math.atan2(endUp.dot(refRight), endUp.dot(refUp));
  }

  const frames: Frame[] = new Array(n);
  let prevRoll: number | null = null;
  for (let i = 0; i < n; i++) {
    const s = sValues[i];
    // Keep s=L on the end side of the wrap so we don't snap to s=0 before unwrap.
    const sRoll = closed && s >= L - 1e-9 ? L : centerline.normalizeS(s);
    let rollDeg = rollAt(rollKeys, sRoll, L, closed);
    if (closed && prevRoll !== null) {
      rollDeg = prevRoll + shortestAngleDelta(prevRoll, rollDeg);
    }
    prevRoll = rollDeg;

    const t = tan[i];
    const u0 = rmfUp[i];
    const r0 = new Vector3().crossVectors(t, u0).normalize();
    const corr = closed && n > 2 ? -(holonomy * i) / (n - 1) : 0;
    const angle = (rollDeg * Math.PI) / 180 + corr;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const u = u0
      .clone()
      .multiplyScalar(cos)
      .addScaledVector(r0, sin)
      .normalize();
    const r = new Vector3().crossVectors(t, u).normalize();
    frames[i] = {
      s,
      pos: pos[i],
      tan: t,
      up: u,
      right: r,
      rollDeg,
      railPos: pos[i].clone().addScaledVector(u, -HEARTLINE_OFFSET),
    };
  }
  return frames;
}

/**
 * Raw RMF frames with closed-track holonomy cancelled — no upright bias, no
 * authored roll. Used to measure how far RMF drifts from gravity-upright.
 */
export function buildRawRmfFrames(centerline: Centerline, sValues: number[]): Frame[] {
  const n = sValues.length;
  if (n === 0) return [];
  const closed = centerline.track.closed;

  const pos: Vector3[] = new Array(n);
  const tan: Vector3[] = new Array(n);
  for (let i = 0; i < n; i++) {
    pos[i] = centerline.posAt(sValues[i]);
    tan[i] = centerline.tanAt(sValues[i]);
  }
  const rmfUp = transportRmf(pos, tan);

  let holonomy = 0;
  if (closed && n > 2) {
    const endUp = rmfUp[n - 1];
    const refUp = rmfUp[0];
    const refRight = new Vector3().crossVectors(tan[n - 1], refUp).normalize();
    holonomy = Math.atan2(endUp.dot(refRight), endUp.dot(refUp));
  }

  const frames: Frame[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const corr = closed && n > 2 ? -(holonomy * i) / (n - 1) : 0;
    const t = tan[i];
    const u0 = rmfUp[i];
    const r0 = new Vector3().crossVectors(t, u0).normalize();
    const cos = Math.cos(corr);
    const sin = Math.sin(corr);
    const u = u0
      .clone()
      .multiplyScalar(cos)
      .addScaledVector(r0, sin)
      .normalize();
    const r = new Vector3().crossVectors(t, u).normalize();
    frames[i] = {
      s: sValues[i],
      pos: pos[i],
      tan: t,
      up: u,
      right: r,
      rollDeg: 0,
      railPos: pos[i].clone().addScaledVector(u, -HEARTLINE_OFFSET),
    };
  }
  return frames;
}

/** Evenly spaced s values over the whole track, endpoint included for open tracks. */
export function sampleSValues(centerline: Centerline, spacing: number): number[] {
  const L = centerline.totalLength;
  if (L <= 0) return [];
  const n = Math.max(2, Math.ceil(L / spacing));
  const out: number[] = [];
  const count = centerline.track.closed ? n : n + 1;
  for (let i = 0; i < count; i++) out.push((L * i) / n);
  if (centerline.track.closed) out.push(L); // duplicate of s=0 so strips close cleanly
  return out;
}
